'use client';

import {
  domToReact,
  Element,
  htmlToDOM,
  type DOMNode,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import {
  createElement,
  JSX,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Image, Link, Typography } from '@/components/atoms';
import { List } from '@/components/molecules';
import { cn } from '@/lib/utils/tailwind';

import { EmbededRenderer } from '../Embeded';
import { WysiwygContentProps } from './types';

const isEmbed = (node: DOMNode): boolean =>
  node.type === 'tag' &&
  ((node as Element).name === 'iframe' || containsEmbed(node as Element));

const containsEmbed = (element: Element): boolean =>
  (element.children || []).some(child => isEmbed(child as DOMNode));

export const WysiwygContent = ({
  html,
  className = 'wysiwyg',
}: WysiwygContentProps): ReactElement => {
  const parseInlineStyle = (styleValue?: string): CSSProperties | undefined => {
    if (!styleValue) {
      return undefined;
    }

    return styleValue.split(';').reduce<CSSProperties>((acc, declaration) => {
      const [property, value] = declaration
        .split(':')
        .map(part => part?.trim());
      if (!property || !value) {
        return acc;
      }

      if (property === 'text-align') {
        if (['left', 'right', 'center', 'justify'].includes(value)) {
          acc.textAlign = value as CSSProperties['textAlign'];
        }
      }

      return acc;
    }, {});
  };

  const toClassName = (value?: string): string | undefined => {
    return typeof value === 'string' && value.trim() ? value : undefined;
  };

  const renderChildren = (children: DOMNode[]): ReactNode[] => {
    return children.map((child, index) => {
      switch (child.type) {
        case 'text':
          return (child as unknown as Text).data;
        case 'tag':
          return renderElement(child as Element, index);
        default:
          return null;
      }
    });
  };

  const renderElement = (element: Element, index: number) => {
    const { name, attribs, children } = element;
    const childNodes = children ? renderChildren(children as DOMNode[]) : [];
    const key = attribs?.id || `${name}-${index}`;
    const childTagNames = (children || [])
      .filter(child => child.type === 'tag')
      .map(child => (child as Element).name);

    switch (name) {
      case 'p':
        if (containsEmbed(element)) {
          return <div key={key}>{childNodes}</div>;
        }
        return (
          <Typography.Text
            variant="paragraph"
            className={toClassName(attribs?.class)}
            style={parseInlineStyle(attribs?.style)}
            key={key}
          >
            {childNodes}
          </Typography.Text>
        );
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return (
          <Typography.Heading
            variant={name}
            as={name === 'h1' ? 'h2' : name}
            className={toClassName(attribs?.class)}
            style={parseInlineStyle(attribs?.style)}
            key={key}
          >
            {childNodes}
          </Typography.Heading>
        );
      case 'ul':
        return (
          <List.Root
            type="unordered"
            className="list-disc list-outside pl-6"
            spacing="default"
            style={parseInlineStyle(attribs?.style)}
            key={key}
          >
            {childNodes}
          </List.Root>
        );
      case 'ol':
        return (
          <List.Root
            type="ordered"
            className="list-decimal list-outside pl-6"
            spacing="default"
            style={parseInlineStyle(attribs?.style)}
            key={key}
          >
            {childNodes}
          </List.Root>
        );
      case 'li': {
        const hasBlockChild = childTagNames.some(tag =>
          ['p', 'ul', 'ol', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(
            tag,
          ),
        );
        return (
          <List.Item
            className={`${
              hasBlockChild
                ? '!list-item !my-2 !cursor-default !rounded-none !transition-none'
                : '!list-item !cursor-default !rounded-none !transition-none'
            } text-text-primary-inverted`}
            style={parseInlineStyle(attribs?.style)}
            key={key}
          >
            {childNodes}
          </List.Item>
        );
      }
      case 'a':
        return (
          <Link
            href={attribs.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline"
            key={key}
          >
            {childNodes}
          </Link>
        );
      case 'img':
        return (
          <Image
            src={attribs?.src || ''}
            alt={attribs?.alt || 'Image'}
            wysiwyg={true}
            key={key}
          />
        );
      case 'iframe': {
        const iframeAttrs = Object.entries(attribs || {})
          .map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`)
          .join(' ');
        const iframeHtml = `<iframe ${iframeAttrs}></iframe>`;
        return <EmbededRenderer html={iframeHtml} key={key} />;
      }
      case 'script': {
        const scriptAttrs = Object.entries(attribs || {})
          .map(([k, v]) => `${k}="${String(v).replace(/"/g, '&quot;')}"`)
          .join(' ');
        const scriptHtml = `<script ${scriptAttrs}></script>`;
        return <EmbededRenderer html={scriptHtml} key={key} />;
      }
      case 'br':
        return <br key={index} />;
      case 'hr':
        return <hr key={index} />;
      default:
        return createElement(
          name,
          { key, style: parseInlineStyle(attribs?.style) },
          childNodes,
        );
    }
  };

  const options: HTMLReactParserOptions = {
    replace: (domNode: DOMNode, index: number): JSX.Element | string | null => {
      if (domNode.type === 'text') {
        return domNode.data;
      }

      if (domNode.type === 'tag' || domNode.type === 'script') {
        return renderElement(domNode as Element, index);
      }

      return null;
    },
  };

  const dom = htmlToDOM(html) as DOMNode[];
  const hasEmbed = dom.some(isEmbed);
  const content = domToReact(dom, options);

  return <div className={cn(className, hasEmbed && 'w-full')}>{content}</div>;
};
